import React, { useEffect, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CContainer,
  CSpinner,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormInput,
  CButtonGroup,
  CButton,
  CCollapse,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilChevronBottom, cilChevronRight } from "@coreui/icons";
import settingsStore from "../../store/SettingsStore";
import DashboardFilters from "../UI/PeriodFilters/DashboardFilters";
import MenuInsightPanel from "../Insights/MenuInsightPanel";
import { getDishStats } from "../../api/iikoApi";
import { formatYmd } from "../../utils/dateRange";
import { logReportView } from "../../api/reportLogApi";

const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");

const SORT_OPTIONS = [
  { key: "qty_desc", label: "Продажи ↓", by: "qty", dir: -1 },
  { key: "qty_asc", label: "Продажи ↑", by: "qty", dir: 1 },
  { key: "revenue_desc", label: "Выручка ↓", by: "revenue", dir: -1 },
  { key: "revenue_asc", label: "Выручка ↑", by: "revenue", dir: 1 },
  { key: "name_asc", label: "По названию", by: "name", dir: 1 },
];

function sortDishes(dishes, sortKey) {
  const sort = SORT_OPTIONS.find((s) => s.key === sortKey) ?? SORT_OPTIONS[0];
  return [...dishes].sort((a, b) => {
    if (sort.by === "name") return sort.dir * String(a.name).localeCompare(String(b.name), "ru");
    return sort.dir * (a[sort.by] - b[sort.by]);
  });
}

function DishRows({ dishes }) {
  return dishes.map((d) => (
    <CTableRow key={d.id}>
      <CTableDataCell>{d.name}</CTableDataCell>
      <CTableDataCell className="text-end">{fmt(d.qty)}</CTableDataCell>
      <CTableDataCell className="text-end">{fmt(d.revenue)} ₽</CTableDataCell>
    </CTableRow>
  ));
}

function CategoryGroup({ group, open, onToggle }) {
  return (
    <div className="border-bottom">
      <button
        type="button"
        onClick={onToggle}
        className="btn d-flex w-100 align-items-center justify-content-between py-2 px-0 border-0 bg-transparent text-start"
      >
        <span className="d-flex align-items-center gap-2">
          <CIcon icon={open ? cilChevronBottom : cilChevronRight} size="sm" />
          <span className="fw-semibold">{group.category}</span>
          <span className="text-body-secondary small">({group.count})</span>
        </span>
        <span className="text-body-secondary small">{fmt(group.revenue)} ₽</span>
      </button>
      <CCollapse visible={open}>
        <CTable hover responsive small className="mb-2">
          <CTableHead>
            <CTableRow>
              <CTableHeaderCell scope="col">Блюдо</CTableHeaderCell>
              <CTableHeaderCell scope="col" className="text-end">
                Продано
              </CTableHeaderCell>
              <CTableHeaderCell scope="col" className="text-end">
                Выручка
              </CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          <CTableBody>
            <DishRows dishes={group.dishes} />
          </CTableBody>
        </CTable>
      </CCollapse>
    </div>
  );
}

const DishStatsPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [quickRange, setQuickRange] = useState("week");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("qty_desc");
  const [openCategories, setOpenCategories] = useState(() => new Set());
  // Committed period, decoupled from the live date pickers — the AI menu panel must
  // only re-fetch on a real "Обновить" click, not on every date clicked while browsing.
  const [appliedRange, setAppliedRange] = useState({ from: null, to: null });

  const load = useCallback(
    (from, to) => {
      if (!selectedServer || !from || !to) return;
      setLoading(true);
      setError(null);
      setAppliedRange({ from, to });
      getDishStats(selectedServer, from, to, false, null, { full: true })
        .then(setStats)
        .catch(setError)
        .finally(() => setLoading(false));
    },
    [selectedServer],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      const today = new Date();
      const past = new Date(today);
      past.setDate(today.getDate() - 7);
      setFromDate(formatYmd(past));
      setToDate(formatYmd(today));
      logReportView({
        event: "report_view",
        reportKey: "dish-stats",
        reportTitle: "Статистика по блюдам",
        userId,
        path: "/dish-stats",
      });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (!selectedServer || !fromDate || !toDate) return;
    load(fromDate, toDate);
  }, [selectedServer, load]);

  const handleRefresh = (from, to) => {
    setFromDate(from);
    setToDate(to);
    load(from, to);
  };

  const search_ = search.trim().toLowerCase();

  const filteredSorted = useMemo(() => {
    const dishes = Array.isArray(stats?.dishes) ? stats.dishes : [];
    const filtered = search_ ? dishes.filter((d) => d.name?.toLowerCase().includes(search_)) : dishes;
    return sortDishes(filtered, sortKey);
  }, [stats, search_, sortKey]);

  // Grouped-by-category view is the default (keeps a long menu scannable); a search
  // switches to a flat sorted list since the user is looking for one specific item.
  const groupedByCategory = useMemo(() => {
    if (search_) return null;
    const map = new Map();
    for (const d of filteredSorted) {
      const cat = d.category || "Без категории";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(d);
    }
    const groups = [...map.entries()].map(([category, dishes]) => ({
      category,
      dishes,
      count: dishes.length,
      revenue: dishes.reduce((a, b) => a + b.revenue, 0),
    }));
    groups.sort((a, b) => b.revenue - a.revenue);
    return groups;
  }, [filteredSorted, search_]);

  const toggleCategory = (cat) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };
  const expandAll = () => setOpenCategories(new Set((groupedByCategory || []).map((g) => g.category)));
  const collapseAll = () => setOpenCategories(new Set());

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Статистика по блюдам</h4>
      <DashboardFilters
        servers={settingsStore.servers}
        selectedServer={selectedServer}
        onServerChange={(e) => setSelectedServer(e.target.value)}
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onRefresh={handleRefresh}
        maxDays={92}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={loading}
        loading={loading}
      />

      {error && (
        <CAlert color="danger" className="mt-3">
          Не удалось загрузить статистику по блюдам
        </CAlert>
      )}

      <MenuInsightPanel serverId={selectedServer} dateFrom={appliedRange.from} dateTo={appliedRange.to} />

      <CCard className="mt-4">
        <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span>
            Все блюда в продаже
            {stats && (
              <span className="text-body-secondary small ms-2">
                {stats.sold_count} из {stats.active_menu_count} продавались за период
              </span>
            )}
          </span>
          <Link to="/dashboard" className="btn btn-sm btn-outline-primary">
            На дашборд
          </Link>
        </CCardHeader>
        <CCardBody>
          <div className="small text-body-secondary mb-3">
            Учитываются только позиции, находящиеся в продаже сейчас — снятые с продажи блюда
            в список не попадают. Категория — папка меню товара в номенклатуре iiko.
          </div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <CFormInput
              type="text"
              placeholder="Поиск по названию…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 280 }}
            />
            <CButtonGroup size="sm">
              {SORT_OPTIONS.map((opt) => (
                <CButton
                  key={opt.key}
                  color="primary"
                  variant={sortKey === opt.key ? undefined : "outline"}
                  onClick={() => setSortKey(opt.key)}
                >
                  {opt.label}
                </CButton>
              ))}
            </CButtonGroup>
            {groupedByCategory && (
              <CButtonGroup size="sm" className="ms-auto">
                <CButton color="secondary" variant="outline" onClick={expandAll}>
                  Развернуть все
                </CButton>
                <CButton color="secondary" variant="outline" onClick={collapseAll}>
                  Свернуть все
                </CButton>
              </CButtonGroup>
            )}
          </div>

          {loading && (
            <div className="text-center py-4">
              <CSpinner />
            </div>
          )}

          {!loading && groupedByCategory && (
            <>
              {groupedByCategory.map((group) => (
                <CategoryGroup
                  key={group.category}
                  group={group}
                  open={openCategories.has(group.category)}
                  onToggle={() => toggleCategory(group.category)}
                />
              ))}
              {groupedByCategory.length === 0 && (
                <div className="text-body-secondary small py-3">Нет данных.</div>
              )}
            </>
          )}

          {!loading && !groupedByCategory && (
            <>
              <CTable hover responsive small>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell scope="col">Блюдо</CTableHeaderCell>
                    <CTableHeaderCell scope="col" className="text-end">
                      Продано
                    </CTableHeaderCell>
                    <CTableHeaderCell scope="col" className="text-end">
                      Выручка
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  <DishRows dishes={filteredSorted} />
                </CTableBody>
              </CTable>
              {filteredSorted.length === 0 && (
                <div className="text-body-secondary small py-3">Ничего не найдено.</div>
              )}
              <p className="text-body-secondary small mt-2 mb-0">Показано: {filteredSorted.length}</p>
            </>
          )}
        </CCardBody>
      </CCard>
    </CContainer>
  );
});

export default DishStatsPage;
